import React, { useState, useEffect, useRef, useCallback } from "react";

import { useWsNavigate } from "../hooks/useWsNavigate";
import { metricsAPI } from "../services/api";
import PageHeader from "../components/layout/PageHeader";
import { useAuth } from "../contexts/AuthContext";
import { useAgent } from "../contexts/AgentContext";
import AgentPicker from "../components/AgentPicker";
import { usePageTitle } from "../contexts/PageTitleContext";
import usePlan from "../hooks/usePlan";
import {
  Sparkles,
  CheckCircle,
  XCircle,
  Minus,
  Plus,
} from "lucide-react";

// Max metrics that can be generated in a single batch (front-end guard).
const MAX_METRICS = 10;

const GenerateMetrics = () => {
  const { setPageTitle, setParentPage, setShowBackButton } = usePageTitle();
  useEffect(() => {
    setPageTitle("Generate Metrics");
    setParentPage({ name: "Metrics", href: "/evaluation/metrics" });
    setShowBackButton(true);
    return () => {
      setPageTitle(null);
      setParentPage(null);
      setShowBackButton(false);
    };
  }, [setPageTitle, setParentPage, setShowBackButton]);
  const navigate = useWsNavigate();
  const { currentWorkspace, user } = useAuth();
  const { agents, selectedAgent: agentFromContext, setSelectedAgent } = useAgent();
  const [loading, setLoading] = useState(false);
  const [generatedMetrics, setGeneratedMetrics] = useState([]);
  const [error, setError] = useState(null);
  const [editingMetric, setEditingMetric] = useState(null);
  const [deletingMetric, setDeletingMetric] = useState(null);
  const [numberOfMetrics, setNumberOfMetrics] = useState(1);
  const [focusArea, setFocusArea] = useState("");

  const { planType, planBadge } = usePlan();
  const [progress, setProgress] = useState("");
  const pollRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const pollForResult = useCallback((generationId) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const res = await metricsAPI.getGenerateStatus(currentWorkspace.id, generationId);
        const data = res.data;
        if (data.status === "completed") {
          stopPolling();
          setGeneratedMetrics(data.metrics || []);
          setLoading(false);
          setProgress("");
        } else if (data.status === "failed") {
          stopPolling();
          setError(data.error || "Generation failed");
          setLoading(false);
          setProgress("");
        } else {
          setProgress(data.progress || "Processing...");
        }
      } catch {
        stopPolling();
        setError("Failed to check generation status");
        setLoading(false);
        setProgress("");
      }
    }, 2000);
  }, [currentWorkspace.id, stopPolling]);

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const handleGenerate = async () => {
    if (!agentFromContext?.id) {
      setError("No agent selected. Please select an agent from the sidebar first.");
      return;
    }

    const count = parseInt(numberOfMetrics, 10);
    if (!count || count < 1 || count > MAX_METRICS) {
      setError(`Please enter a number of metrics between 1 and ${MAX_METRICS}.`);
      return;
    }

    setLoading(true);
    setError(null);
    setProgress("Starting generation...");
    try {
      const response = await metricsAPI.generateMetrics(
        agentFromContext.id,
        currentWorkspace.id,
        { numberOfMetrics: count, focusArea }
      );
      const { generation_id } = response.data;
      pollForResult(generation_id);
    } catch (err) {
      setError("Failed to generate metrics");
      console.error("Error generating metrics:", err);
      setLoading(false);
      setProgress("");
    }
  };

  const handleApprove = async () => {
    setLoading(true);
    try {
      await metricsAPI.createGeneratedMetrics(
        generatedMetrics,
        currentWorkspace.id,
        agentFromContext?.id
      );
      navigate("/evaluation/metrics");
    } catch (err) {
      setError("Failed to create metrics");
      console.error("Error creating metrics:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (metric, index) => {
    setEditingMetric({ ...metric, index });
  };

  const handleDelete = (index) => {
    setDeletingMetric(index);
  };

  const handleDeleteConfirm = () => {
    const newMetrics = generatedMetrics.filter(
      (_, index) => index !== deletingMetric
    );
    setGeneratedMetrics(newMetrics);
    setDeletingMetric(null);
  };

  const handleSaveEdit = (updatedMetric) => {
    const newMetrics = [...generatedMetrics];
    newMetrics[editingMetric.index] = updatedMetric;
    setGeneratedMetrics(newMetrics);
    setEditingMetric(null);
  };

  const TitleCaseText = ({ text }) => {
    const toTitleCase = (str) =>
      str
        .replace(/_/g, " ")
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

    return <span>{toTitleCase(text)}</span>;
  };

  const renderMetricConfig = (metric) => {
    switch (metric.metrics_type) {
      case "specific_text_/_phrase_check":
        return (
          <div className="space-y-3">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-50 flex items-center justify-center">
                <svg
                  className="w-4 h-4 text-indigo-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 6h16M4 12h16m-7 6h7"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">
                  Text/Phrase to check
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {metric.metrics_type_config.text}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-50 flex items-center justify-center">
                <svg
                  className="w-4 h-4 text-indigo-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">
                  Allow Variations
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {metric.metrics_type_config.allow_fuzzy ? "Yes" : "No"}
                </p>
              </div>
            </div>
          </div>
        );
      case "permitted_options_check":
        return (
          <div className="space-y-3">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-50 flex items-center justify-center">
                <svg
                  className="w-4 h-4 text-indigo-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">
                  Allowed values in permitted options
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {metric.metrics_type_config.allowed_values.map(
                    (value, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800"
                      >
                        {value}
                      </span>
                    )
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-50 flex items-center justify-center">
                <svg
                  className="w-4 h-4 text-indigo-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">
                  Condition Match
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {metric.metrics_type_config.condition_match}
                </p>
              </div>
            </div>
          </div>
        );
      case "custom_evaluation":
        return (
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-50 flex items-center justify-center">
              <svg
                className="w-4 h-4 text-indigo-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">LLM Instructions</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {metric.llm_instructions || "Custom evaluation"}
              </p>
            </div>
          </div>
        );
      default:
        return (
          <div className="bg-muted/50 rounded-lg p-4">
            <pre className="text-sm text-muted-foreground overflow-x-auto">
              {JSON.stringify(metric.metrics_type_config, null, 2)}
            </pre>
          </div>
        );
    }
  };

  const renderStructuredOutput = (metric) => {
    return (
      <div className="space-y-3">
        {metric.structured_output.map((logic, idx) => (
          <div key={idx} className="bg-muted/50 rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="flex items-start space-x-2">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-50 flex items-center justify-center">
                  <svg
                    className="w-3 h-3 text-indigo-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground">If</p>
                  <p className="mt-1 text-sm text-foreground">
                    {logic.condition}
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-2">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-50 flex items-center justify-center">
                  <svg
                    className="w-3 h-3 text-indigo-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground">
                    Then Set
                  </p>
                  <p className="mt-1 text-sm text-foreground">
                    {logic.set_metrics_name}
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-2">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-50 flex items-center justify-center">
                  <svg
                    className="w-3 h-3 text-indigo-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground">As</p>
                  <p className="mt-1 text-sm text-foreground">
                    {logic.as_metrics_expected_value}
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-2">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-50 flex items-center justify-center">
                  <svg
                    className="w-3 h-3 text-indigo-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground">
                    Outcome Classification
                  </p>
                  <p className="mt-1 text-sm text-foreground capitalize">
                    {logic.outcome_classification?.replace(/_/g, " ") || "-"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderTriggerState = (metric) => {
    if (metric.evaluation_trigger?.type === "always") {
      return (
        <div className="bg-muted rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <svg
              className="w-5 h-5 text-emerald-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="text-sm font-medium text-foreground">
              This evaluation will be triggered always in a call
            </p>
          </div>
        </div>
      );
    } else {
      return (
        <div className="bg-muted rounded-lg p-4">
          <div className="flex items-start space-x-2">
            <svg
              className="w-5 h-5 text-muted-foreground mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div>
              <p className="text-sm font-medium text-foreground">
                This evaluation will be triggered when the call reaches a
                specific state
              </p>
              {metric.evaluation_trigger?.state_prompt && (
                <p className="mt-2 text-sm text-foreground">
                  State Prompt: {metric.evaluation_trigger.state_prompt}
                </p>
              )}
            </div>
          </div>
        </div>
      );
    }
  };

  const renderEditForm = (metric) => {
    return (
      <div className="space-y-6">
        {/* Metrics Information */}
        <div className="bg-card rounded-xl shadow-sm border border-border p-4">
          <h3 className="text-sm font-semibold text-foreground mb-6">
            Metrics Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <label className="block text-xs font-medium text-foreground mb-2">
                Metrics Name
              </label>
              <input
                type="text"
                value={metric.name}
                onChange={(e) =>
                  setEditingMetric({ ...metric, name: e.target.value })
                }
                className="mt-1 block w-full rounded-lg border-input bg-background shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm transition duration-150 ease-in-out"
                placeholder="Enter metrics name"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-2">
                Metrics Type
              </label>
              <select
                value={metric.metrics_type}
                onChange={(e) =>
                  setEditingMetric({ ...metric, metrics_type: e.target.value })
                }
                className="mt-1 block w-full rounded-lg border-input bg-background shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm transition duration-150 ease-in-out"
              >
                <option value="specific_text_/_phrase_check">
                  Specific Text/Phrase Check
                </option>
                <option value="permitted_options_check">
                  Permitted Options Check
                </option>
                <option value="custom_evaluation">
                  Custom Evaluation
                </option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-2">
                Result Type
              </label>
              <select
                value={metric.result_type || "boolean"}
                onChange={(e) =>
                  setEditingMetric({ ...metric, result_type: e.target.value })
                }
                className="mt-1 block w-full rounded-lg border-input bg-background shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm transition duration-150 ease-in-out"
              >
                <option value="boolean">Boolean (Pass / Fail)</option>
                <option value="rating">Rating (1-5)</option>
                {metric.metrics_type !== "specific_text_/_phrase_check" && (
                  <option value="enum">Enum (Custom Values)</option>
                )}
                <option value="numeric">Numeric</option>
              </select>
            </div>
          </div>
        </div>

        {/* Configuration */}
        <div className="bg-card rounded-xl shadow-sm border border-border p-4">
          <h3 className="text-sm font-semibold text-foreground mb-6">
            Configuration
          </h3>
          <div className="space-y-8">
            {metric.metrics_type === "specific_text_/_phrase_check" && (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-foreground">
                    Text/Phrase
                  </label>
                  <input
                    type="text"
                    value={metric.metrics_type_config.text}
                    onChange={(e) =>
                      setEditingMetric({
                        ...metric,
                        metrics_type_config: {
                          ...metric.metrics_type_config,
                          text: e.target.value,
                        },
                      })
                    }
                    className="mt-1 block w-full rounded-md border-input bg-background shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                    placeholder="Enter text or phrase to check"
                  />
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={metric.metrics_type_config.allow_fuzzy}
                    onChange={(e) =>
                      setEditingMetric({
                        ...metric,
                        metrics_type_config: {
                          ...metric.metrics_type_config,
                          allow_fuzzy: e.target.checked,
                        },
                      })
                    }
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-input rounded"
                  />
                  <label className="ml-2 block text-sm text-foreground">
                    Allow Variations
                  </label>
                </div>
              </div>
            )}
            {metric.metrics_type === "permitted_options_check" && (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-foreground">
                    Enum Allowed Values
                  </label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {metric.metrics_type_config.allowed_values.map(
                      (val, idx) => (
                        <span
                          key={idx}
                          className="flex items-center bg-muted rounded px-2 py-1 text-xs"
                        >
                          <input
                            type="text"
                            value={val}
                            onChange={(e) => {
                              const newValues = [
                                ...metric.metrics_type_config.allowed_values,
                              ];
                              newValues[idx] = e.target.value;
                              setEditingMetric({
                                ...metric,
                                metrics_type_config: {
                                  ...metric.metrics_type_config,
                                  allowed_values: newValues,
                                },
                              });
                            }}
                            className="bg-transparent border-none focus:ring-0 w-20 text-sm"
                          />
                          <button
                            type="button"
                            className="ml-1 text-muted-foreground hover:text-red-500"
                            onClick={() => {
                              const newValues =
                                metric.metrics_type_config.allowed_values.filter(
                                  (_, i) => i !== idx
                                );
                              setEditingMetric({
                                ...metric,
                                metrics_type_config: {
                                  ...metric.metrics_type_config,
                                  allowed_values: newValues,
                                },
                              });
                            }}
                          >
                            &times;
                          </button>
                        </span>
                      )
                    )}
                    <button
                      type="button"
                      className="ml-2 px-2 py-1 bg-indigo-100 text-indigo-700 rounded"
                      onClick={() => {
                        setEditingMetric({
                          ...metric,
                          metrics_type_config: {
                            ...metric.metrics_type_config,
                            allowed_values: [
                              ...metric.metrics_type_config.allowed_values,
                              "",
                            ],
                          },
                        });
                      }}
                    >
                      + Add Value
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground">
                    Condition Match
                  </label>
                  <select
                    value={metric.metrics_type_config.condition_match}
                    onChange={(e) =>
                      setEditingMetric({
                        ...metric,
                        metrics_type_config: {
                          ...metric.metrics_type_config,
                          condition_match: e.target.value,
                        },
                      })
                    }
                    className="mt-1 block w-full rounded-md border-input bg-background shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                  >
                    <option value="At least one value from the list must be present">
                      At least one value from the list must be present
                    </option>
                    <option value="Exactly one value from the list must be present">
                      Exactly one value from the list must be present
                    </option>
                    <option value="None of the values from the list should be present">
                      None of the values from the list should be present
                    </option>
                  </select>
                </div>
              </div>
            )}
            {/* LLM Instructions — shown for all metric types */}
            <div>
              <label className="block text-xs font-medium text-foreground">
                LLM Instructions
              </label>
              <textarea
                value={metric.llm_instructions || ""}
                onChange={(e) =>
                  setEditingMetric({
                    ...metric,
                    llm_instructions: e.target.value,
                  })
                }
                rows={4}
                className="mt-1 block w-full rounded-md border-input bg-background shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                placeholder="Provide instructions for the LLM on how to evaluate this metric..."
              />
            </div>
          </div>
        </div>

        {/* Structured Output */}
        <div className="bg-card rounded-xl shadow-sm border border-border p-4">
          <h3 className="text-sm font-semibold text-foreground mb-6">
            Structured Output
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-2">
            <div className="text-xs font-medium text-foreground">If</div>
            <div className="text-xs font-medium text-foreground">Then Set</div>
            <div className="text-xs font-medium text-foreground">As</div>
            <div className="text-xs font-medium text-foreground">Outcome</div>
          </div>
          {metric.structured_output.map((logic, index) => (
            <div
              key={index}
              className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-2"
            >
              <input
                type="text"
                value={logic.condition}
                onChange={(e) => {
                  const newLogic = [...metric.structured_output];
                  newLogic[index] = {
                    ...newLogic[index],
                    condition: e.target.value,
                  };
                  setEditingMetric({ ...metric, structured_output: newLogic });
                }}
                className="block w-full rounded-md border-input bg-background shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                placeholder="Write your condition here"
              />
              <input
                type="text"
                value={logic.set_metrics_name}
                onChange={(e) => {
                  const newLogic = [...metric.structured_output];
                  newLogic[index] = {
                    ...newLogic[index],
                    set_metrics_name: e.target.value,
                  };
                  setEditingMetric({ ...metric, structured_output: newLogic });
                }}
                className="block w-full text-sm rounded-md border-input bg-background shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="Metric Name"
              />
              <input
                type="text"
                value={logic.as_metrics_expected_value}
                onChange={(e) => {
                  const newLogic = [...metric.structured_output];
                  newLogic[index] = {
                    ...newLogic[index],
                    as_metrics_expected_value: e.target.value,
                  };
                  setEditingMetric({ ...metric, structured_output: newLogic });
                }}
                className="block w-full rounded-md border-input bg-background shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                placeholder="Expected Value"
              />
              <select
                value={logic.outcome_classification || ""}
                onChange={(e) => {
                  const newLogic = [...metric.structured_output];
                  newLogic[index] = {
                    ...newLogic[index],
                    outcome_classification: e.target.value,
                  };
                  setEditingMetric({ ...metric, structured_output: newLogic });
                }}
                className="block w-full rounded-md border-input bg-background shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
              >
                <option value="">Select Outcome</option>
                <option value="meets_expectations">Meets Expectations</option>
                <option value="exceeds_expectations">
                  Exceeds Expectations
                </option>
                <option value="requires_attention">Requires Attention</option>
                <option value="goal_achieved">Goal Achieved</option>
                <option value="goal_missed">Goal Missed</option>
              </select>
            </div>
          ))}
          <button
            type="button"
            onClick={() => {
              setEditingMetric({
                ...metric,
                structured_output: [
                  ...metric.structured_output,
                  {
                    condition: "",
                    set_metrics_name: "",
                    as_metrics_expected_value: "",
                    outcome_classification: "",
                  },
                ],
              });
            }}
            className="mt-2 inline-flex items-center px-3 py-2 border border-input shadow-sm text-sm font-medium rounded-md text-foreground bg-card hover:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            + Add Logic Row
          </button>
        </div>

        {/* Trigger State */}
        <div className="bg-card rounded-xl shadow-sm border border-border p-4">
          <h3 className="text-sm font-semibold text-foreground mb-6">
            Trigger State
          </h3>
          <div className="space-y-6">
            <div>
              <label className="block text-xs font-medium text-foreground mb-2">
                Trigger Type
              </label>
              <select
                value={metric.evaluation_trigger?.type || "call_state"}
                onChange={(e) =>
                  setEditingMetric({
                    ...metric,
                    evaluation_trigger: {
                      ...metric.evaluation_trigger,
                      type: e.target.value,
                    },
                  })
                }
                className="mt-1 block w-full rounded-lg border-input bg-background shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm transition duration-150 ease-in-out"
              >
                <option value="always">Always (Respecting Scope)</option>
                <option value="call_state">
                  If Call Reaches Specific State
                </option>
                <option value="custom">Let me decide</option>
              </select>
            </div>
            <div
              className={`transition-all duration-300 ease-in-out ${
                metric.evaluation_trigger?.type === "call_state"
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 -translate-y-4 h-0 overflow-hidden"
              }`}
            >
              <label className="block text-xs font-medium text-foreground mb-2">
                State Prompt
              </label>
              <textarea
                value={metric.evaluation_trigger?.state_prompt || ""}
                onChange={(e) =>
                  setEditingMetric({
                    ...metric,
                    evaluation_trigger: {
                      ...metric.evaluation_trigger,
                      state_prompt: e.target.value,
                    },
                  })
                }
                rows={3}
                className="mt-1 block w-full rounded-lg border-input bg-background shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm transition duration-150 ease-in-out"
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
      <div className="px-4 min-h-screen">
        <PageHeader
          title="Generate Metrics"
          description="Generate metrics using AI for your agent."
          breadcrumbs={[
            { label: "Evaluation", path: "/evaluation" },
            { label: "Metrics", path: "/evaluation/metrics" },
            { label: "Generate Metrics", path: "/evaluation/generate-metrics" },
          ]}
          planBadge={planBadge}
        />

        <div className="mt-6">
          {generatedMetrics.length === 0 && (
          <div className="flex flex-col space-y-5">
            <div className="grid grid-cols-2 gap-4">
              {/* Agent — active picker in place of the previous disabled input */}
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">
                  Agent
                </label>
                <AgentPicker
                  agents={agents}
                  selectedAgent={agentFromContext}
                  setSelectedAgent={setSelectedAgent}
                  onAdd={() => navigate("/ai-testing-agent/create")}
                />
              </div>

              {/* Number of metrics */}
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">
                  Number of metrics
                </label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    aria-label="Decrease"
                    disabled={(parseInt(numberOfMetrics, 10) || 1) <= 1}
                    onClick={() => setNumberOfMetrics((prev) => Math.max(1, (parseInt(prev, 10) || 1) - 1))}
                    className="flex-shrink-0 h-9 w-9 flex items-center justify-center rounded-lg border border-input text-foreground hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <input
                    type="number"
                    min={1}
                    max={MAX_METRICS}
                    value={numberOfMetrics}
                    onChange={(e) => {
                      const raw = e.target.value;
                      // Allow clearing while typing so a fresh number can be
                      // entered instead of appending to "1". Min is normalized
                      // on blur; only the max is capped here.
                      if (raw === "") {
                        setNumberOfMetrics("");
                        return;
                      }
                      const n = parseInt(raw, 10);
                      if (Number.isNaN(n)) return;
                      setNumberOfMetrics(Math.min(MAX_METRICS, Math.max(0, n)));
                    }}
                    onBlur={() => setNumberOfMetrics((prev) => Math.min(MAX_METRICS, Math.max(1, parseInt(prev, 10) || 1)))}
                    className="block w-full text-center rounded-lg border-input bg-background shadow-sm focus:border-black focus:ring-black text-sm"
                  />
                  <button
                    type="button"
                    aria-label="Increase"
                    disabled={(parseInt(numberOfMetrics, 10) || 1) >= MAX_METRICS}
                    onClick={() => setNumberOfMetrics((prev) => Math.min(MAX_METRICS, (parseInt(prev, 10) || 0) + 1))}
                    className="flex-shrink-0 h-9 w-9 flex items-center justify-center rounded-lg border border-input text-foreground hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Up to {MAX_METRICS} at a time.
                </p>
              </div>
            </div>

            {/* Focus area / instructions */}
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">
                Focus area / additional instructions
                <span className="text-muted-foreground font-normal ml-1">(optional)</span>
              </label>
              <textarea
                value={focusArea}
                onChange={(e) => setFocusArea(e.target.value)}
                rows={3}
                className="block w-full rounded-lg border-input bg-background shadow-sm focus:border-black focus:ring-black text-sm"
                placeholder="e.g. Focus on compliance and regulatory metrics, or generate metrics that evaluate customer satisfaction and empathy..."
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                {loading && progress && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <svg className="animate-spin h-4 w-4 text-muted-foreground" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {progress}
                  </div>
                )}
                {error && (
                  <div className="flex items-center text-sm text-red-600">
                    <XCircle className="h-4 w-4 mr-1.5 flex-shrink-0" />
                    {error}
                  </div>
                )}
              </div>
              <button
                onClick={handleGenerate}
                disabled={loading || !agentFromContext?.id}
                className="inline-flex items-center px-5 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generate Metrics
                  </>
                )}
              </button>
            </div>
          </div>
          )}

        {generatedMetrics.length > 0 && (
          <div className="mt-6 bg-card rounded-lg shadow-lg border border-border p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-sm font-semibold text-foreground">
                AI Generated Metrics
              </h3>
              <button
                onClick={handleApprove}
                disabled={loading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary/40 disabled:opacity-50"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Approve All
              </button>
            </div>
            <div className="space-y-6">
              {generatedMetrics.map((metric, index) => (
                <div
                  key={index}
                  className="bg-card rounded-xl shadow-base border border-border p-4 hover:shadow-md transition-shadow duration-200"
                >
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex w-full">
                      <div className="w-1/2 flex items-center">
                        <span className="text-xs font-medium text-muted-foreground mr-2">
                          Metric Name:
                        </span>
                        <h4 className="text-sm font-semibold text-foreground truncate">
                          {metric.name}
                        </h4>
                      </div>
                      <div className="w-1/2 flex items-center">
                        <span className="text-xs font-medium text-muted-foreground mr-2">
                          Metric Type:
                        </span>
                        <span className="flex items-center text-sm text-foreground">
                          {/* Icon for metric type */}
                          {metric.metrics_type ===
                            "specific_text_/_phrase_check" && (
                            <svg
                              className="w-5 h-5 text-indigo-500 mr-1"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M4 6h16M4 12h16m-7 6h7"
                              />
                            </svg>
                          )}
                          {metric.metrics_type ===
                            "permitted_options_check" && (
                            <svg
                              className="w-5 h-5 text-green-500 mr-1"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                              />
                            </svg>
                          )}
                          {metric.metrics_type === "custom_evaluation" && (
                            <svg
                              className="w-5 h-5 text-yellow-500 mr-1"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                          )}
                          <TitleCaseText text={metric.metrics_type} />
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => handleEdit(metric, index)}
                        className="p-2 text-muted-foreground hover:text-foreground focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 rounded-lg"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDelete(index)}
                        className="p-2 text-muted-foreground hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 rounded-lg"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <hr className="border-t border-border mb-4" />
                  <div className="space-y-4">
                    <div>
                      <h5 className="text-sm font-semibold text-foreground mb-2">
                        Trigger State
                      </h5>
                      {renderTriggerState(metric)}
                    </div>
                    <div>
                      <h5 className="text-sm font-semibold text-foreground mb-2">
                        Configuration
                      </h5>
                      {renderMetricConfig(metric)}
                    </div>
                    <div>
                      <h5 className="text-sm font-semibold text-foreground mb-2">
                        Structured Output
                      </h5>
                      {renderStructuredOutput(metric)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Edit Modal — Full Screen */}
        {editingMetric && (
          <div className="fixed inset-0 z-50 bg-background overflow-y-auto">
            <div className="sticky top-0 z-10 bg-card border-b border-border px-6 py-3 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setEditingMetric(null)}
                  className="p-1 text-muted-foreground hover:text-foreground rounded-md"
                >
                  <XCircle className="h-5 w-5" />
                </button>
                <h2 className="text-sm font-semibold text-foreground">
                  Edit Metric
                </h2>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setEditingMetric(null)}
                  className="px-4 py-2 text-sm font-medium text-foreground bg-card border border-input rounded-md hover:bg-muted/50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleSaveEdit(editingMetric)}
                  className="px-4 py-2 text-sm font-medium text-white bg-black border border-transparent rounded-md hover:bg-black/80"
                >
                  Save Changes
                </button>
              </div>
            </div>
            <div className="px-6 py-4">
              {renderEditForm(editingMetric)}
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deletingMetric !== null && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4">
              <div
                className="fixed inset-0 bg-black bg-opacity-30 transition-opacity"
                onClick={() => setDeletingMetric(null)}
              />
              <div className="relative bg-card rounded-xl shadow-xl max-w-md w-full p-4 z-10">
                <h3 className="text-sm font-semibold text-foreground mb-2">
                  Delete Metric
                </h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Are you sure you want to remove this metric from the generated
                  list? This action cannot be undone.
                </p>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setDeletingMetric(null)}
                    className="px-4 py-2 text-sm font-medium text-foreground bg-card border border-input rounded-md hover:bg-muted/50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteConfirm}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
  );
};

export default GenerateMetrics;
